import React, { useEffect, useRef, useState } from 'react';
import { SlidersHorizontal, Braces, ChevronDown } from 'lucide-react';
import SliderRow from 'shared/components/ui/SliderRow';
import JsonEditor from 'features/prompts/components/JsonEditor';

/**
 * ModelParamsPopover — a "Params" button that opens the generation knobs.
 *
 * Two tabs over ONE object, the same contract as the prompt editor's model
 * block: the sliders cover what most calls need, and JSON covers what they
 * can't — provider-specific keys the form has no control for. Editing is a
 * patch, never a replace, so a key added in JSON survives a slider drag and
 * flipping between tabs is lossless.
 *
 * An unset param is ABSENT from the object rather than zero. That distinction
 * matters: omitting `temperature` means "provider default", while `0` is a
 * deliberate, very different instruction.
 */

const SLIDERS = [
  ['temperature', 'Temperature', 0, 2, 0.1],
  ['max_tokens', 'Max tokens', 0, 16384, 128],
  ['top_p', 'Top p', 0, 1, 0.05],
];

const FORM_KEYS = new Set(SLIDERS.map(([k]) => k));

export default function ModelParamsPopover({ value, onChange, disabled, label = 'Params' }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('form');
  const wrapRef = useRef(null);
  const params = value && typeof value === 'object' && !Array.isArray(value) ? value : {};

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const patch = (p) => {
    const next = { ...params };
    for (const [k, v] of Object.entries(p)) {
      if (v === '' || v === null || v === undefined) delete next[k];
      else next[k] = v;
    }
    onChange(next);
  };

  // Count only what's actually set, so the chip reflects real configuration.
  const setCount = Object.keys(params).filter((k) => params[k] !== '' && params[k] != null).length;
  const extraKeys = Object.keys(params).filter((k) => !FORM_KEYS.has(k));

  const tabCls = (t) => `inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[12px] font-medium transition-colors ${
    tab === t
      ? 'bg-[color:var(--surface-base)] text-[color:var(--text-primary)] shadow-sm'
      : 'text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]'
  }`;

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 rounded-lg border border-[color:var(--border-default)] px-2.5 py-1.5 text-[12px] font-medium text-[color:var(--text-muted)] transition-colors hover:text-[color:var(--text-primary)] disabled:opacity-40"
      >
        <SlidersHorizontal size={13} />
        {label}
        {setCount > 0 && (
          <span className="text-[11px] font-normal text-[color:var(--text-disabled)]">{setCount}</span>
        )}
        <ChevronDown size={13} className={`transition-transform ${open ? 'rotate-180' : ''} text-[color:var(--text-disabled)]`} />
      </button>

      {/* Right-aligned: the button sits at the right edge of whatever holds it
          — the evaluator form's model row, an agent step's settings panel —
          and a left-aligned 300px card hangs off that edge and is clipped.
          Aligning to the button's right keeps it on screen in both, and
          `max-w` keeps it inside a panel narrower than the card. */}
      {open && (
        <div className="absolute right-0 top-[calc(100%+4px)] z-40 w-[300px] max-w-[calc(100vw-2rem)] rounded-lg border border-[color:var(--border-default)] bg-[color:var(--surface-raised)] p-3 shadow-lg">
          <div className="mb-3 inline-flex items-center gap-0.5 rounded-lg border border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] p-0.5">
            <button type="button" onClick={() => setTab('form')} className={tabCls('form')}>
              <SlidersHorizontal size={12} /> Configure
            </button>
            <button type="button" onClick={() => setTab('json')} className={tabCls('json')}>
              <Braces size={12} /> JSON
            </button>
          </div>

          {tab === 'json' ? (
            <JsonEditor
              value={params}
              emptyValue={{}}
              disabled={disabled}
              height="180px"
              onChange={(next) => onChange(next)}
            />
          ) : (
            <div className="flex flex-col gap-3.5">
              {SLIDERS.map(([key, sliderLabel, min, max, step]) => (
                <SliderRow
                  key={key}
                  label={sliderLabel}
                  value={params[key]}
                  min={min}
                  max={max}
                  step={step}
                  disabled={disabled}
                  onChange={(v) => patch({ [key]: v })}
                  onClear={() => patch({ [key]: '' })}
                />
              ))}
              {extraKeys.length > 0 && (
                <p className="text-[11px] text-[color:var(--text-muted)]">
                  Also set, editable in JSON: <span className="font-mono">{extraKeys.join(', ')}</span>
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
